import { PaintBrush, Wrench } from '@phosphor-icons/react'
import {
  AutomatedActions,
  AutomatedActionsSelectionType,
  AutomationFlowJourneySteps,
  AutomationStateType,
  PerformedActionsOptions,
  AutomationSortOrderType,
  AutomationStatus
} from 'src/lib/types/automations'
import { CartConditionOptions } from 'src/lib/types/common/cartConditions'
import { CustomerEligibilityOptions } from 'src/lib/types/common/customerConditions'
import { JourneyLineStepType } from 'src/lib/types/common/journeyLine'
import { RadioItemConfigType, SwitchableTabType } from 'src/lib/types/random'

export const initialAutomationFlowState: AutomationStateType = {
  title: '',
  description: '',
  automatedAction: {
    type: AutomatedActions.NONE
  },
  performedAction: {
    type: PerformedActionsOptions.NO_CONDITIONS
  },
  cartConditions: {
    type: CartConditionOptions.NO_CONDITIONS
  },
  customerEligibility: {
    type: CustomerEligibilityOptions.ALL_CUSTOMERS
  },
  activePeriod: {
    startDate: new Date(),
    endDateEnabled: false
  },
  isActive: false,
  channelIds: []
}

export const journeyLineConfig: JourneyLineStepType[] = [
  {
    label: 'Setup',
    step: AutomationFlowJourneySteps.SETUP
  },
  {
    label: 'Cart condition',
    step: AutomationFlowJourneySteps.CART_CONDITIONS
  },
  {
    label: 'Customer eligibility',
    step: AutomationFlowJourneySteps.CUSTOMER_ELIGIBILITY
  },
  {
    label: 'Usage limits',
    step: AutomationFlowJourneySteps.USAGE_LIMITS
  }
]

export const automatedActionsSelection: AutomatedActionsSelectionType[] = [
  {
    label: 'Hide payment methods',
    value: AutomatedActions.HIDE_PAYMENT_MODES
  },
  {
    label: 'Disable rewards',
    value: AutomatedActions.DISABLE_REWARDS
  }
]

export const automationsTabList: SwitchableTabType[] = [
  {
    id: 'tab-create-automation',
    isActive: true,
    label: 'Create automations',
    name: 'CREATE_AUTOMATIONS',
    childComponent: 'CreateAutomationsTab'
  },
  {
    id: 'tab-view-automation',
    isActive: false,
    label: 'View automations',
    name: 'VIEW_AUTOMATIONS',
    childComponent: 'ViewAutomationsTab'
  }
]

export const performedActionSelection = [
  { label: 'Redeem rewards', value: PerformedActionsOptions.REDEEM_REWARDS },
  { label: 'Apply discounts', value: PerformedActionsOptions.APPLY_DISCOUNTS },
  { label: 'No conditions', value: PerformedActionsOptions.NO_CONDITIONS }
]

export const AUTOMATION_MENU_ITEMS = [
  {
    category: 'Payment',
    items: [
      {
        id: AutomatedActions.HIDE_PAYMENT_MODES,
        value: AutomatedActions.HIDE_PAYMENT_MODES,
        label: 'Block Payment',
        description: 'Hide specific payment methods for the customer',
        status: AutomationStatus.ACTIVE,
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      }
    ]
  },
  {
    category: 'Reward',
    items: [
      {
        id: AutomatedActions.DISABLE_REWARDS,
        value: AutomatedActions.DISABLE_REWARDS,
        label: 'Disable Reward redemption',
        description: 'Customer will not be able to redeem rewards',
        status: AutomationStatus.ACTIVE,
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      },
      {
        label: 'Conditional Reward visibility',
        status: AutomationStatus.COMING_SOON,
        description: 'Hide rewards based on cart conditions',
        value: 'CONDITIONAL_REWARD_VISIBILITY',
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      }
    ]
  },
  {
    category: 'Cart',
    items: [
      {
        label: 'Disable Cart CTA',
        status: AutomationStatus.COMING_SOON,
        description: 'Disable the checkout button in cart',
        value: 'DISABLE_CART_CTA',
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      },
      {
        label: 'Disable Cart quantity editor',
        status: AutomationStatus.COMING_SOON,
        description: 'Prevent customers from changing item quantities',
        value: 'DISABLE_CART_QUANTITY_EDITOR',
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      }
    ]
  },
  {
    category: 'Shipping',
    items: [
      {
        label: 'Free shipping threshold',
        status: AutomationStatus.COMING_SOON,
        description: 'Set a minimum cart value for free shipping',
        value: 'FREE_SHIPPING_THRESHOLD',
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      },
      {
        label: 'Zone-based rate adjustments',
        status: AutomationStatus.COMING_SOON,
        description: 'Adjust shipping rates based on customer location',
        value: 'ZONE_BASED_RATE_ADJUSTMENTS',
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      },
      {
        label: 'Time-based rate changes',
        status: AutomationStatus.COMING_SOON,
        description: 'Change shipping rates based on time of day',
        value: 'TIME_BASED_RATE_CHANGES',
        UiConfig: {
          icon: Wrench,
          color: 'bg-cyan-500'
        }
      }
    ]
  },
  {
    category: 'Customise',
    items: [
      {
        label: 'Customise Login page',
        status: AutomationStatus.COMING_SOON,
        description: 'Customize the look and feel of the login page',
        value: 'CUSTOMISE_LOGIN_PAGE',
        UiConfig: {
          icon: PaintBrush,
          color: 'bg-amber-500'
        }
      },
      {
        label: 'Customise Address page',
        status: AutomationStatus.COMING_SOON,
        description: 'Customize the address entry form',
        value: 'CUSTOMISE_ADDRESS_PAGE',
        UiConfig: {
          icon: PaintBrush,
          color: 'bg-amber-500'
        }
      },
      {
        label: 'Customise Payment page',
        status: AutomationStatus.COMING_SOON,
        description: 'Customize the payment selection screen',
        value: 'CUSTOMISE_PAYMENT_PAGE',
        UiConfig: {
          icon: PaintBrush,
          color: 'bg-amber-500'
        }
      },
      {
        label: 'Customise Cart page',
        status: AutomationStatus.COMING_SOON,
        description: 'Customize the cart summary view',
        value: 'CUSTOMISE_CART_PAGE',
        UiConfig: {
          icon: PaintBrush,
          color: 'bg-amber-500'
        }
      }
    ]
  }
]

export const AutomationListSortOptions: { key: AutomationSortOrderType; value: string }[] = [
  { key: 'NEWEST', value: 'Newest First' },
  { key: 'OLDEST', value: 'Oldest First' },
  { key: 'NAME_ASC', value: 'Name Ascending' },
  { key: 'NAME_DESC', value: 'Name Descending' }
]

export const CART_CONDITION_TAB_OPTIONS = ['AMOUNT', 'QUANTITY'] as const

export const AUTOMATION_EXCLUDED_PAYMENT_MODES = ['LOYALTY', 'REWARDS_WALLET', 'SIMPL', 'SIMPL_PL', 'FREE']

export const PRO_TIPS = {
  [AutomatedActions.HIDE_PAYMENT_MODES]:
    'Hiding specific payment modes like COD for high-risk orders can help reduce RTO.',
  [AutomatedActions.DISABLE_REWARDS]:
    'Disabling reward redemption prevents double discounting when other offers are already applied.'
}

export const WHEN_SECTION_KEYS = ['cart-conditions', 'customer-eligibility', 'customer-action']
export const THEN_SECTION_KEYS = ['automation-action', 'automation-channels', 'automation-availability']
